import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { deleteAccount, logout } from "../redux/authSlice";
import { Lock, Shield, LogOut, Trash2, Power, AlertTriangle } from "lucide-react";

const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  const maskedUser = user.length <= 2 ? user + "***" : user.slice(0, 2) + "***";
  return `${maskedUser}@${domain}`;
};

const LoginSecurity: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { userId } = useParams<{ userId: string }>();
  const id = userId || "guest";

  const [editingPassword, setEditingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await dispatch(deleteAccount()).unwrap();
      await dispatch(logout()).unwrap();
      navigate("/AuthSwitch");
    } catch (err: any) {
      alert(err?.message || "Failed to delete account.");
    } finally {
      setLoading(false);
    }
  };

  const firstName = user?.firstName || "";
  const lastName = user?.lastName || "";
  const email = user?.emailAddress ? maskEmail(user.emailAddress) : "hidden@mail.com";

  return (
    <div style={styles.wrapper}>
      <header style={styles.header}>
        <h1 style={styles.headerTitle}>Login & Security</h1>
      </header>

      {/* User header info */}
      <div style={styles.profileHeader}>
        <h2 style={styles.userName}>{firstName} {lastName}</h2>
        <p style={styles.userEmail}>{email}</p>
      </div>

      <div style={styles.container}>
        {/* Password */}
        <motion.div style={styles.card} whileHover={{ scale: 1.02 }}>
          <div style={styles.rowBetween}>
            <div style={styles.iconRow}>
              <Lock size={18} color="#bf9b30" />
              <span style={styles.sectionTitle}>Password</span>
            </div>
            {!editingPassword && (
              <button style={styles.editBtn} onClick={() => setEditingPassword(true)}>
                Edit
              </button>
            )}
          </div>

          <AnimatePresence>
            {editingPassword && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                style={styles.passwordBox}
              >
                <input type="password" placeholder="New password" style={styles.input} />
                <input
                  type="password"
                  placeholder="Confirm password"
                  style={styles.input}
                />
                <div style={styles.rowBetween}>
                  <button
                    style={styles.cancelBtn}
                    onClick={() => setEditingPassword(false)}
                  >
                    Cancel
                  </button>
                  <button style={styles.updateBtn}>Update Password</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* 2-Step Auth */}
        <motion.div style={styles.card} whileHover={{ scale: 1.02 }}>
          <div style={styles.iconRow}>
            <Shield size={18} color="#bf9b30" />
            <span style={styles.sectionTitle}>2-Step Authentication</span>
          </div>
        </motion.div>

        {/* Sessions */}
        <motion.div style={styles.card} whileHover={{ scale: 1.02 }}>
          <div style={styles.iconRow}>
            <LogOut size={18} color="#bf9b30" />
            <span style={styles.sectionTitle}>Active Sessions</span>
          </div>
        </motion.div>

        {/* Deactivate */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => setShowDeactivateConfirm(true)}
          style={styles.deactivateBtn}
        >
          <Power size={18} style={{ marginRight: 8 }} />
          Deactivate Account
        </motion.button>

        {/* Delete */}
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
          onClick={() => setShowDeleteConfirm(true)}
          style={styles.deleteBtn}
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          {loading ? "Deleting..." : "Delete Account"}
        </motion.button>
      </div>

      {/* Confirm Overlays */}
      <AnimatePresence>
        {(showDeleteConfirm || showDeactivateConfirm) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={styles.overlay}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              style={styles.alertCard}
            >
              <AlertTriangle color="#f5b700" size={36} />
              <h3 style={styles.alertTitle}>
                {showDeleteConfirm
                  ? "Delete Account?"
                  : "Deactivate Account?"}
              </h3>
              <p style={styles.alertText}>
                {showDeleteConfirm
                  ? "This action is permanent and cannot be undone."
                  : "You can reactivate anytime by logging in again."}
              </p>
              <div style={styles.alertActions}>
                <button
                  style={styles.cancelBtn}
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setShowDeactivateConfirm(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  style={
                    showDeleteConfirm
                      ? styles.dangerBtn
                      : styles.updateBtn
                  }
                  onClick={
                    showDeleteConfirm
                      ? handleDelete
                      : () => setShowDeactivateConfirm(false)
                  }
                >
                  {showDeleteConfirm ? "Delete" : "Deactivate"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginSecurity;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "100px 20px 60px",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  header: {
    position: "fixed",
    top: 0,
    width: "100%",
    height: 80,
    background: "rgba(0,0,0,0.85)",
    borderBottom: "1px solid rgba(191,155,48,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    backdropFilter: "blur(8px)",
  },
  headerTitle: {
    color: "#bf9b30",
    fontWeight: 600,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  profileHeader: {
    textAlign: "center",
    marginTop: 20,
  },
  userName: {
    fontSize: 20,
    fontWeight: 600,
    color: "#fff",
    marginBottom: 4,
  },
  userEmail: {
    color: "#999",
    fontSize: 14,
  },
  container: {
    width: "100%",
    maxWidth: 520,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    marginTop: 20,
  },
  card: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.25)",
    borderRadius: 16,
    padding: "18px 20px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.6)",
  },
  iconRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: "#e6e6e6",
  },
  rowBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  editBtn: {
    background: "none",
    border: "1px solid rgba(191,155,48,0.4)",
    color: "#bf9b30",
    padding: "4px 14px",
    borderRadius: 20,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  passwordBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginTop: 12,
  },
  input: {
    background: "#000",
    border: "1px solid rgba(191,155,48,0.25)",
    borderRadius: 8,
    color: "#fff",
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    fontWeight: 500,
  },
  cancelBtn: {
    flex: 1,
    background: "linear-gradient(145deg, #111, #1a1a1a)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 500,
    padding: "10px",
    cursor: "pointer",
    marginRight: 10,
  },
  updateBtn: {
    flex: 1,
    background: "linear-gradient(145deg, #bf9b30, #e6c35a)",
    border: "none",
    borderRadius: 8,
    color: "#000",
    fontWeight: 600,
    padding: "10px",
    cursor: "pointer",
  },
  deactivateBtn: {
    background: "linear-gradient(145deg, #bf9b30, #e6c35a)",
    border: "1px solid rgba(191,155,48,0.3)",
    borderRadius: 12,
    color: "#000",
    fontWeight: 600,
    padding: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  deleteBtn: {
    background: "linear-gradient(145deg, #2a0000, #4a0000)",
    border: "1px solid rgba(255,0,0,0.3)",
    borderRadius: 12,
    color: "#fff",
    fontWeight: 600,
    padding: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  alertCard: {
    background: "linear-gradient(145deg, #1a0000, #000)",
    border: "1px solid rgba(255,0,0,0.3)",
    borderRadius: 16,
    padding: 30,
    textAlign: "center",
    width: 320,
    boxShadow: "0 0 20px rgba(255,0,0,0.3)",
  },
  alertTitle: {
    color: "#f5b700",
    fontWeight: 700,
    fontSize: 18,
    margin: "10px 0",
  },
  alertText: {
    color: "#ccc",
    fontSize: 14,
    marginBottom: 20,
  },
  alertActions: {
    display: "flex",
    gap: 12,
  },
  dangerBtn: {
    flex: 1,
    background: "linear-gradient(145deg, #8b0000, #ff1a1a)",
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontWeight: 600,
    padding: "10px",
    cursor: "pointer",
  },
};
